
import moment from "moment";
import { useBookingSystemStore } from "../../stores/bookingSystemStore";


/**
 * @classdesc Une option de module
 * @kind class
 * @export
 * @prop {int} id - Id de l'option
 *  @prop {int} id_module - Id du module
 * @prop {string} nom - Nom de l'option
 *  @prop {int} prix - Prix de base de l'option
 * @prop {boolean} obligatoire - L'option est obligatoire
 * @prop {datetime} date_max - Date/Heure limite de disponibilité
 * @prop {boolean} visible_accueil - L'option est visible sur l'accueil du module
 * @prop {int} quantite - Quantite de l'option
 * @class Option
 */
export default class Option {
  id = "";
  id_module = "";
  nom = "";
  prix = "";
  obligatoire = 0;
  date_max = "";
  heure_max = "";
  date_min = "";
  heure_min = "";
  visible_accueil = 1;
  information_variante_tarif = "";
  information_supplementaire = "";
  quantite = 0;
  nb_inscrits = 0;
  quantite_souhaite = 0;
  disponible_changement = 1;
  list_liaisons_course = [];
  list_liaisons_pack = [];
  metadatas = [];
  nb_pre_inscrits = 0;

  /**
   * Crée une option
   * @constructs
   * @kind function
   * @memberof Option
   */
  constructor() { }

  /**
   * Défini une option à partir d'un jeu de données
   *
   * @param {Object} data
   * @memberof Option
   */
  set(data) {
    for (const [key, value] of Object.entries(data)) {
      if (this.hasOwnProperty(key)) {
        // Cloner les tableaux et objets pour éviter le partage de références
        if (Array.isArray(value)) {
          this[key] = value.map(item => {
            if (typeof item === 'object' && item !== null) {
              return { ...item };
            }
            return item;
          });
        } else if (typeof value === 'object' && value !== null) {
          this[key] = { ...value };
        } else {
          this[key] = value;
        }
      }
    }
    for (let i = 0; i < this.list_liaisons_course.length; i++) {
      this.list_liaisons_course[i].selected = true;
    }
    for (let i = 0; i < this.list_liaisons_pack.length; i++) {
      this.list_liaisons_pack[i].selected = true;
    }
  }

  update_cart(vip_ins = null, lien_groupe = null) {

    let cart = { nb_articles: 0, prix: 0, prix_regulier: 0 };
    cart.nb_articles = parseInt(this.quantite_souhaite);
    const prix_normal = parseInt(this.quantite_souhaite) * parseFloat(this.prix);
    cart.prix_regulier = prix_normal;
    cart.prix = prix_normal;
    if (vip_ins && vip_ins.doit_payer_option == '0') {
      cart.prix = 0;
    }
    if (lien_groupe && !lien_groupe.doit_payer_options) {
      cart.prix = 0;
    }

    this.current_price = cart.prix;
    this.cart = cart;
    return cart;
  }

  get_infos_supp_accueil() {
    let infos = "";
    for (let i = 0; i < this.metadatas.length; i++) {
      if (this.metadatas[i].nom == "info_supp_accueil") {
        infos = " - " + this.metadatas[i].valeur;
      }
    }
    return infos;
  }

  get_info_variante() {
    let infos = "";
    for (let i = 0; i < this.metadatas.length; i++) {
      if (this.metadatas[i].nom == "info_variante") {
        infos = this.metadatas[i].valeur;
      }
    }
    return infos;
  }

  has_bouton_renvoi_billetterie() {
    for (let i = 0; i < this.metadatas.length; i++) {
      if (this.metadatas[i].nom == "bouton_renvoi_billetterie" && this.metadatas[i].valeur == "1") {
        return true;
      }
    }
    return false;
  }


  is_complet() {
    if (this.quantite == 0) return false;
    return parseInt(this.nb_inscrits) >= parseInt(this.quantite);
  }

  is_closed() {
    if (moment(this.date_max).isValid()) {
      let now = moment();
      let date_max = moment(this.date_max + " " + this.heure_max);
      if (now.isAfter(date_max)) {
        return true;
      }
    }
    if (moment(this.date_min).isValid()) {
      let now = moment();
      let date_min = moment(this.date_min + " " + this.heure_min);
      if (now.isBefore(date_min)) {
        return true;
      }
    }
    return false;
  }


  handle_minimum_quantity() {
    if (!this.get_metadata("quantite_min")) return;
    let min = parseInt(this.get_metadata("quantite_min"));
    if (this.quantite_souhaite < min) {
      this.quantite_souhaite = min;
    }
  }

  get_max_quantite() {
    let max = 30;
    for (let i = 0; i < this.metadatas.length; i++) {
      if (this.metadatas[i].nom == "quantite_max") {
        max = parseInt(this.metadatas[i].valeur);
        break;
      }
    }
    return max;
  }

  get_metadata(nom) {
    let value = "";
    for (let i = 0; i < this.metadatas.length; i++) {
      if (this.metadatas[i].nom == nom) {
        value = this.metadatas[i].valeur;
        return value;
      }
    }
    return false;
  }



  get_places_disponibles(all_options, options_coureur, list_inscriptions, vip_ins = null, on_add_option_form = false, list_commandes_options = [], initial_commande_inscription, omit_quantite_souhaite = false, current_inscription_index = null, lien_groupe = null) {
    const DEFAULT_MAX = 30;
    let places_disponibles = {};
    if (vip_ins && vip_ins.acces_options_completes == '1') {
      return DEFAULT_MAX;
    }
    if (lien_groupe && lien_groupe?.is_lien_groupe) {
      const inscriptions_options_completes = lien_groupe.inscriptions_options_completes;
      if (inscriptions_options_completes) {
        return DEFAULT_MAX;
      }
    }

    places_disponibles.default = DEFAULT_MAX; // Par défaut
    places_disponibles.max_reservable_item = this.get_max_quantite(); // Quantité maximale sélectionnable pour cette option

    places_disponibles.max_reservable_item -= omit_quantite_souhaite ? 0 : this.quantite_souhaite; // On retire la quantité déjà souhaitée
    if (on_add_option_form) { // Si on est sur le formulaire d'ajout d'option
      places_disponibles.max_reservable_item -= this.get_quantite_already_bought(list_commandes_options); // On retire la quantité déjà achetée
    }
    if (initial_commande_inscription) {
      places_disponibles.max_reservable_item -= this.get_quantite_already_bought(initial_commande_inscription.list_commandes_options); // On retire la quantité déjà achetée
    }

    places_disponibles.quantite_restante_item = this.get_quantite_item_disponible(); // Quantité restante pour cette option
    places_disponibles.quantite_restante_item -= this.get_quantite_souhaite_on_commande(list_inscriptions, on_add_option_form); // On retire la quantité déjà souhaitée sur la commande


    places_disponibles.max_reservable_group = this.get_max_reservable_group(); // Quantité maximale sélectionnable pour ce groupe d'options
    places_disponibles.max_reservable_group -= this.get_quantite_souhaite_groupe(options_coureur); // On retire la quantité déjà souhaitée
    if (on_add_option_form) { // Si on est sur le formulaire d'ajout d'option
      places_disponibles.max_reservable_group -= this.get_quantite_already_bought_group(list_commandes_options); // On retire la quantité déjà achetée
    }
    if (initial_commande_inscription) {
      places_disponibles.max_reservable_group -= this.get_quantite_already_bought_group(initial_commande_inscription.list_commandes_options); // On retire la quantité déjà achetée
    }

    places_disponibles.max_reservable_par_equipe = this.get_max_reservable_par_equipe(); // Quantité maximale sélectionnable pour chaque équipe
    if (places_disponibles.max_reservable_par_equipe < 30) {
      places_disponibles.max_reservable_par_equipe -= this.get_quantite_souhaite_on_equipe(list_inscriptions, current_inscription_index, on_add_option_form);
    }


    places_disponibles.quantite_restante_group = this.get_quantite_group_disponible(all_options); // Quantité restante pour ce groupe d'option
    places_disponibles.quantite_restante_group -= this.get_quantite_souhaite_groupe_on_commande(list_inscriptions, on_add_option_form); // On retire la quantité déjà souhaitée sur la commande
    let min_places_disponibles = Math.min(places_disponibles.default,
      places_disponibles.max_reservable_item,
      places_disponibles.quantite_restante_item,
      places_disponibles.max_reservable_group,
      places_disponibles.max_reservable_par_equipe,
      places_disponibles.quantite_restante_group);

    min_places_disponibles = Math.max(min_places_disponibles, 0);
    return min_places_disponibles; // On retourne le minimum des places disponibles

  }

  get_max_reservable_par_equipe() {
    let quantite_max = this.get_metadata("quantite_max_par_equipe");
    if (!quantite_max) return 30;
    return parseInt(quantite_max);
  }



  /**
 * Returns the quantity of this option that has already been bought.
 * @param {Array} list_commandes_options - The list of command options.
 * @returns {number} - The quantity of this option that has already been bought.
 */
  get_quantite_already_bought(list_commandes_options) {
    let quantite = 0;
    let option_already_buy = list_commandes_options.find((option) => {
      return option.id_item == this.id && option.type == "option";
    });
    if (option_already_buy) {
      quantite += parseInt(option_already_buy.quantite);
    }
    return quantite;
  }

  /**
 * Returns the quantity of available items for this option.
 * @returns {number|boolean} The quantity of available items, or false if the quantity is zero.
 */
  get_quantite_item_disponible() {

    if (parseInt(this.quantite) == 0) return 30;
    let quantite_disponible = parseInt(this.quantite) - parseInt(this.nb_inscrits);
    return quantite_disponible < 0 ? 0 : quantite_disponible;
  }

  /**
  * Calculates the total quantity of a specific option on a command.
  * 
  * @param {Array} list_inscriptions - The list of inscriptions.
  * @param {boolean} [on_add_option_form=false] - Indicates if the option is being added on the form.
  * @returns {number} - The total quantity of the option on the command.
  */
  get_quantite_souhaite_on_commande(list_inscriptions, on_add_option_form = false) {
    let quantite = 0;
    for (let i = 0; i < list_inscriptions.length; i++) {
      let inscription = list_inscriptions[i];
      let option = null;
      if (on_add_option_form) {
        option = inscription.list_options_amended.find((option) => {
          return option.id == this.id;
        });
      }
      else {
        option = inscription.list_options.list.find((option) => {
          return option.id == this.id;
        });
      }
      if (option) {
        quantite += parseInt(option.quantite_souhaite);
      }
    }
    return quantite;
  }

  /**
   * Calcule la quantité souhaitée de l'option pour les autres coureurs de la même équipe.
   * 
   * @param {Array} list_inscriptions - La liste des inscriptions
   * @param {number} current_inscription_index - Index de l'inscription courante
   * @param {boolean} [on_add_option_form=false] - Indique si on est sur le formulaire d'ajout d'option
   * @returns {number} - La quantité souhaitée de l'option pour les autres coureurs de la même équipe
   */
  get_quantite_souhaite_on_equipe(list_inscriptions, current_inscription_index, on_add_option_form = false) {
    let quantite = 0;
    if (!current_inscription_index) return 0;

    // Accéder au store des équipes
    const bookingSystemStore = useBookingSystemStore();
    const equipes = bookingSystemStore.equipes.list;

    // Trouver l'équipe de l'inscription courante
    let current_equipe = null;
    for (let i = 0; i < equipes.length; i++) {
      const equipe = equipes[i];
      for (let j = 0; j < equipe.coureurs.length; j++) {
        if (equipe.coureurs[j].index === current_inscription_index) {
          current_equipe = equipe;
          break;
        }
      }
      if (current_equipe) break;
    }

    // Si l'inscription courante n'est pas dans une équipe, retourner 0
    if (!current_equipe) {
      return 0;
    }

    // Parcourir les coureurs de la même équipe (en excluant l'inscription courante)
    for (let j = 0; j < current_equipe.coureurs.length; j++) {
      const coureur = current_equipe.coureurs[j];
      const inscription_index = coureur.index;

      // Exclure l'inscription courante
      if (inscription_index === current_inscription_index) {
        continue;
      }

      // Vérifier que l'index est valide
      if (inscription_index >= 0 && inscription_index < list_inscriptions.length) {
        const inscription = list_inscriptions[inscription_index];

        // Chercher l'option dans cette inscription
        let option = null;
        if (on_add_option_form) {
          option = inscription.list_options_amended.find((option) => {
            return option.id == this.id;
          });
        } else {
          option = inscription.list_options.list.find((option) => {
            return option.id == this.id;
          });
        }

        // Si l'option existe, ajouter sa quantité souhaitée
        if (option) {
          quantite += parseInt(option.quantite_souhaite);
        }
      }
    }

    return quantite;
  }

  /**
 * Retrieves the maximum number of reservable groups for this option.
 * @returns {number|boolean} The maximum number of reservable groups, or false if not found.
 */
  get_max_reservable_group() {
    for (let i = 0; i < this.metadatas.length; i++) {
      if (this.metadatas[i].nom == "max_groupe_option") {
        return parseInt(this.metadatas[i].valeur);
      }
    }
    return 30;
  }

  /**
  * 
  * @param {*} options_coureur - La liste des selections de l'utilisateur courant pour les options
  * @returns {number} La quantité souhaitée pour ce groupe d'options
  */
  get_quantite_souhaite_groupe(options_coureur) {
    let quantite = 0;
    let current_group = this.get_metadata("groupe_option");
    if (!current_group) return 0;
    let options_groupe = options_coureur.filter((option) => {
      return option.get_metadata("groupe_option") == current_group;
    });
    for (let j = 0; j < options_groupe.length; j++) {
      quantite += parseInt(options_groupe[j].quantite_souhaite);
    }
    return quantite;
  }

  /**
     * Calculates the total quantity of options already bought for the current group.
     * 
     * @param {Array} list_commandes_options - The list of command options.
     * @returns {number} - The total quantity of options already bought.
     */
  get_quantite_already_bought_group(list_commandes_options) {

    let quantite = 0;
    let current_group = this.get_metadata("groupe_option");
    let options_already_buy = list_commandes_options.filter((option) => {
      return option.type == "option" && option.metadatas.groupe_option == current_group;
    });
    for (let j = 0; j < options_already_buy.length; j++) {
      quantite += parseInt(options_already_buy[j].quantite);
    }
    return quantite;
  }

  /**
  * Calculates the quantity of available group spots for the option.
  * 
  * @param {Array} options_coureur - The list of options for the runner.
  * @returns {number} - The quantity of available group spots.
  */
  get_quantite_group_disponible(options_coureur) {
    let places_reserves = 0;
    let current_group = this.get_metadata("groupe_option");
    let places_total_groupe = this.get_metadata("max_groupe_option");
    if (!places_total_groupe || !current_group) return 30;
    let options_groupe = options_coureur.filter((option) => {
      return option.get_metadata("groupe_option") == current_group;
    });
    for (let j = 0; j < options_groupe.length; j++) {
      places_reserves += parseInt(options_groupe[j].nb_inscrits);
    }
    let places_disponibles = places_total_groupe - places_reserves;
    return places_disponibles < 0 ? 0 : places_disponibles;

  }

  /**
 * Calculates the total quantity of desired options in a specific group for a given list of inscriptions.
 * @param {Array} list_inscriptions - The list of inscriptions.
 * @param {boolean} [on_add_option_form=false] - Indicates whether the function is called from the add option form.
 * @returns {number} The total quantity of desired options in the specified group.
 */
  get_quantite_souhaite_groupe_on_commande(list_inscriptions, on_add_option_form = false) {
    let quantite = 0;
    let current_group = this.get_metadata("groupe_option");
    if (!current_group) return 0;
    for (let i = 0; i < list_inscriptions.length; i++) {
      let inscription = list_inscriptions[i];
      let options_groupe = [];
      if (on_add_option_form) {
        options_groupe = inscription.list_options_amended.filter((option) => {
          return option.get_metadata("groupe_option") == current_group;
        });
      }
      else {
        options_groupe = inscription.list_options.list.filter((option) => {
          return option.get_metadata("groupe_option") == current_group;
        });
      }


      for (let j = 0; j < options_groupe.length; j++) {
        quantite += parseInt(options_groupe[j].quantite_souhaite);
      }
    }
    return quantite;
  }






  get_max_quantite_groupe(all_options) {
    if (!all_options) return 0;
    let groupe = this.get_metadata("groupe_option");

    if (groupe) {
      let max_groupe = parseInt(this.get_metadata("max_groupe_option"));

      let options_groupe = all_options.filter((option) => {
        return option.get_metadata("groupe_option") == groupe;
      });
      for (let i = 0; i < options_groupe.length; i++) {
        max_groupe -= parseInt(options_groupe[i].nb_inscrits);
      }
      return max_groupe;
    }
    return 0;

  }

  get_text_prix() {
    if (parseInt(this.prix) == 0) {
      return "Gratuit";
    } else {
      return this.prix + "€";
    }
  }

  get_max(all_options, quantite_local, vip_ins = null) {

    if (vip_ins) {
      if (vip_ins.acces_options_completes == '1') {
        return this.get_max_quantite();
      }
    }
    let max = this.get_max_quantite();

    let max_groupe = this.get_max_quantite_groupe(all_options);
    if (max_groupe != 0) {
      max = Math.min(max, max_groupe);
    }
    // Si il y a une limite sur l'option
    if (this.quantite != "0") {
      let restant = parseInt(this.quantite) - parseInt(this.nb_inscrits) - quantite_local;
      if (restant < max) {
        max = Math.max(restant, 0);
      }
    }
    // Si il y a une limite sur le groupe mais pas sur l'option
    else if (this.quantite == "0" && max_groupe != 0) {
      let restant = max_groupe - quantite_local;
      if (restant < max) {
        max = Math.max(restant, 0);
      }
    }

    return Math.max(max, 0);
  }

  is_selectionnable(statut_ouverture_module, admin_view, admin_ins, vip_ins, lien_groupe = null) {
    if (lien_groupe && lien_groupe.is_lien_groupe && statut_ouverture_module == 'ouverte') {
      const is_complet = this.is_complet();
      const inscriptions_options_completes = lien_groupe.inscriptions_options_completes;
      if (inscriptions_options_completes) {
        return true;
      }
      return !is_complet;
    }
    if (!this.is_closed() && statut_ouverture_module == 'ouverte' && !this.is_complet()) {
      return true;
    }
    if (admin_view && !this.is_complet()) {
      return true;
    }
    if (admin_ins) {
      return true;
    }
    if (vip_ins && statut_ouverture_module == 'ouverte') {
      return vip_ins.acces_options_completes == '1';
    }
    return false;
  }

  /* 
  * Vérifie si l'option est liée avec des courses ou des packs
  */
  has_liaisons() {
    return this.list_liaisons_course.length > 0 || this.list_liaisons_pack.length > 0;
  }

  /* 
    Vérifie si l'option est disponible dans le formulaire d'ajout d'option
  */
  available_for_ajout_option(commande_inscription) {
    if (this.disponible_changement == 0) {
      return false;
    }
    if (!this.has_liaisons()) {
      return true;
    }
    if (commande_inscription.type == "course") {
      for (let i = 0; i < this.list_liaisons_course.length; i++) {
        if (this.list_liaisons_course[i].id_course == commande_inscription.id_item) {
          return true;
        }
      }
    }
    else if (commande_inscription.type == "pack") {
      for (let i = 0; i < this.list_liaisons_pack.length; i++) {
        if (this.list_liaisons_pack[i].id_pack == commande_inscription.id_item) {
          return true;
        }
      }
    }
    return false;

  }

  /* 
    Vérifie si l'option est disponible avec les courses sélectionnées
  */
  is_available_with_selected_course(list_selected_courses) {
    // Si l'option n'a pas de liaisons avec des courses, elle est disponible
    if (this.list_liaisons_course.length === 0) {
      return true;
    }

    // Vérifie si au moins une liaison correspond à une course sélectionnée
    for (let j = 0; j < this.list_liaisons_course.length; j++) {
      const liaison = this.list_liaisons_course[j];
      const course = list_selected_courses.find((course) => { return course.id == liaison.id_course; });
      if (course) {
        return true;
      }
    }

    // Aucune liaison ne correspond aux courses sélectionnées
    return false;
  }

  is_available_with_selected_pack(list_selected_packs) {
    for (let j = 0; j < this.list_liaisons_pack.length; j++) {
      let liaison = this.list_liaisons_pack[j];
      for (let k = 0; k < list_selected_packs.length; k++) {
        let pack = list_selected_packs[k];
        if (liaison.id_pack == pack.id && pack.selected) {
          return true;
        }
      }
    }
    return false;
  }

  getSimplifiedData() {
    if (this.quantite_souhaite == 0) return null;
    const infos = {};
    infos.prix = this?.cart?.prix;
    infos.quantite = this.quantite_souhaite;
    infos.id = this.id;
    infos.nom = this.nom;
    return infos;
  }

  is_quantite_editable() {
    if (this.metadatas.find(metadata => metadata.nom == "groupe_option" && metadata.valeur != '')) {
      return false;
    }
    if (this.metadatas.find(metadata => metadata.nom == "max_groupe_option" && metadata.valeur != '')) {
      return false;
    }
    return true;
  }

  get_majorations() {
    let list_majorations = [];
    let majorations_metadatas = this.metadatas.filter(metadata => metadata.nom.startsWith("majoration_"));
    for (let i = 1; i < 10; i++) {
      let majoration_date_name = "majoration_" + i + "_date";
      let majoration_tarif_name = "majoration_" + i + "_tarif";
      let date = majorations_metadatas.find(majoration => majoration.nom == majoration_date_name);
      let tarif = majorations_metadatas.find(majoration => majoration.nom == majoration_tarif_name);
      if (!date || !tarif) break;
      list_majorations.push({ date: date.valeur, tarif: tarif.valeur });
    }
    return list_majorations;
  }

  update_majorations(majorations) {
    for (let i = 0; i < majorations.length; i++) {
      let majoration_date_name = "majoration_" + (i + 1) + "_date";
      let majoration_tarif_name = "majoration_" + (i + 1) + "_tarif";
      let date = this.metadatas.find(metadata => metadata.nom == majoration_date_name);
      let tarif = this.metadatas.find(metadata => metadata.nom == majoration_tarif_name);
      if (date && tarif) {
        date.valeur = majorations[i].date;
        tarif.valeur = majorations[i].tarif;
      }
    }
  }
}
